import { CheckCircleFilled, SearchOutlined } from '@ant-design/icons'
import { Card, Flex, Input, Select, Table, Tag } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { BoosterMilestoneDetail, BoosterProgramEnrolledUser } from 'Modules'
import { useEffect, useMemo, useState } from 'react'

import LinkAddress from 'component/link/link-address'
import { BOOSTER_PROGRAM_FIELD_MAX_LENGTH } from 'constants/booster-program'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { useBoosterEnrolledUsers } from 'hooks/booster-program'
import { useDebounce } from 'hooks/use-debounce'

interface Props {
  programId: string
  milestones: BoosterMilestoneDetail[]
}

type StatusFilter = 'all' | 'active' | 'expired'

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
]

export const BoosterEnrolledUsersTable = ({ programId, milestones }: Props) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 400) as string
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [milestoneFilter, setMilestoneFilter] = useState<string>('all')
  const [page, setPage] = useState(DEFAULT_PAGE_INDEX)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)

  // Reset to first page whenever any filter changes.
  useEffect(() => {
    setPage(DEFAULT_PAGE_INDEX)
  }, [debouncedSearch, statusFilter, milestoneFilter])

  const { enrolled, total, isLoading } = useBoosterEnrolledUsers(programId, {
    page,
    limit,
    walletAddress: debouncedSearch.trim() || undefined,
    milestoneId: milestoneFilter === 'all' ? undefined : milestoneFilter,
    status: statusFilter === 'all' ? undefined : statusFilter,
  })

  const milestoneOptions = useMemo(
    () => [
      { value: 'all', label: 'All Milestones' },
      ...milestones.map((m) => ({ value: m.id, label: `M${m.index + 1} (Buy ≥ ${m.threshold})` })),
    ],
    [milestones]
  )

  const columns: ColumnsType<BoosterProgramEnrolledUser> = useMemo(
    () => [
      {
        title: 'User',
        dataIndex: 'walletAddress',
        key: 'walletAddress',
        width: 220,
        render: (value: string) => (
          <span className="booster-detail__enrolled-user-cell">
            <LinkAddress address={value} type="address" />
          </span>
        ),
      },
      {
        title: 'Enrolled On',
        dataIndex: 'enrolledOn',
        key: 'enrolledOn',
        width: 140,
      },
      {
        title: 'Period',
        key: 'period',
        width: 120,
        render: (_, record) =>
          record.status === 'EXPIRED' ? (
            <span className="booster-detail__enrolled-expired">{record.period || 'Expired'}</span>
          ) : (
            <span>{record.period}</span>
          ),
      },
      {
        title: 'Milestone Progress',
        key: 'milestoneProgress',
        width: 60 + milestones.length * 60,
        render: (_, record) => (
          <Flex gap={16} align="center" wrap="wrap">
            {milestones.map((m) => {
              const reached = record.milestonesReached.includes(m.index)
              return (
                <Flex key={m.id} gap={4} align="center" className="booster-detail__milestone-pill">
                  <span className="booster-detail__milestone-pill-label">M{m.index + 1}</span>
                  {reached ? (
                    <CheckCircleFilled className="booster-detail__milestone-pill-check" />
                  ) : (
                    <span className="booster-detail__milestone-pill-empty" />
                  )}
                </Flex>
              )
            })}
          </Flex>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (value: BoosterProgramEnrolledUser['status']) =>
          value === 'ACTIVE' ? (
            <Tag color="success" className="booster-detail__enrolled-status">
              Active
            </Tag>
          ) : (
            <Tag className="booster-detail__enrolled-status booster-detail__enrolled-status--expired">
              Expired
            </Tag>
          ),
      },
    ],
    [milestones]
  )

  return (
    <div className="booster-detail__enrolled">
      <Card className="booster-detail__enrolled-filters">
        <Flex gap={12} wrap="wrap" align="center">
          <Input
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            placeholder="Search by wallet address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            maxLength={BOOSTER_PROGRAM_FIELD_MAX_LENGTH.walletSearch}
            variant="filled"
            className="booster-detail__enrolled-search"
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTIONS}
            variant="filled"
            className="booster-detail__enrolled-select"
          />
          <Select
            value={milestoneFilter}
            onChange={setMilestoneFilter}
            options={milestoneOptions}
            variant="filled"
            className="booster-detail__enrolled-select"
          />
        </Flex>
      </Card>

      <div className="booster-detail__enrolled-total">
        Total Users: <strong>{total.toLocaleString('en-US')}</strong>
      </div>

      <Card className="booster-detail__enrolled-table-card">
        <Table<BoosterProgramEnrolledUser>
          columns={columns}
          dataSource={enrolled}
          rowKey={(r) => r.id}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            simple: true,
            showSizeChanger: false,
            position: ['bottomLeft'],
          }}
          onChange={(pagination: TablePaginationConfig) => {
            if (pagination.current) setPage(pagination.current)
            if (pagination.pageSize) setLimit(pagination.pageSize)
          }}
          scroll={{ x: 1000 }}
          className="booster-detail__enrolled-table"
        />
      </Card>
    </div>
  )
}
